import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import { formatDateTime } from "@/lib/utils";
import { FormField } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { getToastStateFromLocationState } from "@/lib/navigation";
import { useToast } from "@/lib/ui";
import { parseApiError } from "@/lib/error-handling";
import {
  useAdminCheckout,
  useAdminCheckoutApproveParticipants,
  useAdminCheckoutApproveBoats,
  useAdminCheckoutTakeBalanceParticipants,
  useAdminCheckoutPaymentLink,
  useAdminCheckoutCreateUser,
  useAdminCheckoutRefund,
  useAdminParticipantApprove,
  useAdminParticipantReject,
  useAdminParticipantCancel,
  useAdminUser,
  type AdminCheckoutDetail,
} from "@/lib/admin-api";
import {
  findCrewForBoat,
  boatIsPending,
} from "@/lib/boat-utils";
import {
  hasApprovedDepositPayment,
  hasApprovedBalancePayment,
  hasApprovedPaymentCaptured,
  isEligibleForSharedBalanceCollection,
  isBalanceCronExcluded,
  getSharedApplicablePaymentLinkKinds,
  type PaymentLinkKind,
} from "@/lib/payment-status";

type Participant = NonNullable<AdminCheckoutDetail["participants"]>[number];

function formatParticipantGroup(participant: { group?: string | null }) {
  return participant.group?.trim() ? participant.group : "—";
}

function StatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "danger";
      case "CANCELLED":
        return "info";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

function CheckoutStatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "CANCELLED":
        return "info";
      case "PARTIAL":
        return "info";
      case "MIXED":
        return "warning";
      case "COMPLETE":
        return "success";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

export function CheckoutDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();

  const [refundDialogVisible, setRefundDialogVisible] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [takeBalanceDialogMode, setTakeBalanceDialogMode] = useState<
    "selected" | "all" | null
  >(null);
  const [paymentLinkDialogVisible, setPaymentLinkDialogVisible] =
    useState(false);
  const [paymentLinkKind, setPaymentLinkKind] =
    useState<PaymentLinkKind>("balance");
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [selectedBoats, setSelectedBoats] = useState<string[]>([]);
  const [expandedBoatRows, setExpandedBoatRows] = useState<Record<string, boolean> | null>(null);
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [participantIdToReject, setParticipantIdToReject] = useState<
    string | null
  >(null);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState(false);
  const [participantIdToCancel, setParticipantIdToCancel] = useState<
    string | null
  >(null);
  const [approvingParticipantId, setApprovingParticipantId] = useState<
    string | null
  >(null);
  const [loadingCheckoutId, setLoadingCheckoutId] = useState<string | null>(
    null,
  );
  const menuRefs = useRef<Record<string, Menu | null>>({});

  const [createUserDialogVisible, setCreateUserDialogVisible] = useState(false);
  /** "someoneElse" or a name key (firstName + \u241F + lastName) for a unique participant */
  const [createUserPrimaryValue, setCreateUserPrimaryValue] = useState<string>("");
  const [createUserFirstName, setCreateUserFirstName] = useState("");
  const [createUserLastName, setCreateUserLastName] = useState("");
  const [createUserSubUserNames, setCreateUserSubUserNames] = useState<
    Array<{ firstName: string; lastName: string }>
  >([]);

  const checkoutQuery = useAdminCheckout(id);
  const checkout = checkoutQuery.data?.checkout ?? null;

  const userQuery = useAdminUser(checkout?.userId ?? undefined);
  const user = (userQuery.data as { user?: { firstName: string; lastName: string } } | undefined)
    ?.user ?? null;

  const bulkApproveMutation = useAdminCheckoutApproveParticipants();
  const bulkApproveBoatsMutation = useAdminCheckoutApproveBoats();
  const takeBalanceMutation = useAdminCheckoutTakeBalanceParticipants();
  const paymentLinkMutation = useAdminCheckoutPaymentLink();
  const createUserMutation = useAdminCheckoutCreateUser();
  const approveMutation = useAdminParticipantApprove();
  const rejectMutation = useAdminParticipantReject();
  const cancelMutation = useAdminParticipantCancel();
  const refundMutation = useAdminCheckoutRefund();

  useEffect(() => {
    if (!id) {
      void navigate("/checkouts");
    }
  }, [id, navigate]);

  // Show toast from router state
  useEffect(() => {
    if (checkoutQuery.isLoading) return;

    const toastState = getToastStateFromLocationState(location.state);
    if (!toastState) return;

    showToast(toastState);
    void navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.pathname,
    location.state,
    navigate,
    checkoutQuery.isLoading,
    showToast,
  ]);

  const participants = checkout?.participants ?? [];
  const showGroupColumn = participants.some(
    (participant) => participant.event?.groupsEnabled,
  );
  const eventParticipants = participants.filter(p => !p.boatId);
  const pendingParticipants = eventParticipants.filter((p) => p.status === "PENDING");
  const approvedParticipants = eventParticipants.filter((p) => p.status === "APPROVED");

  const boats = checkout?.boats ?? [];
  const pendingBoats = boats.filter((boat) => boatIsPending(boat));

  const createUserNameKey = (firstName: string, lastName: string) =>
    `${firstName}\u241F${lastName}`;
  const uniqueParticipantsByName = useMemo(() => {
    const seen = new Set<string>();
    return participants.filter((p: Participant) => {
      const key = createUserNameKey(p.firstName, p.lastName);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [participants]);
  const createUserPrimaryType: "participant" | "someoneElse" =
    createUserPrimaryValue === "someoneElse" ? "someoneElse" : "participant";
  const createUserPrimaryName =
    createUserPrimaryType === "participant" && createUserPrimaryValue
      ? uniqueParticipantsByName.find(
          (p: Participant) =>
            createUserNameKey(p.firstName, p.lastName) === createUserPrimaryValue,
        )
      : null;

  const hasApprovedWithPayment = participants.some((p) =>
    hasApprovedPaymentCaptured(p),
  );
  const balanceEligibleParticipants = eventParticipants.filter((p) =>
    isEligibleForSharedBalanceCollection(p),
  );
  const selectableParticipants = eventParticipants.filter(
    (participant) =>
      participant.boatId == null &&
      (participant.status === "PENDING" ||
        participant.status === "APPROVED" ||
        isEligibleForSharedBalanceCollection(participant)),
  );
  const selectedEventParticipants = eventParticipants.filter(
    (participant) =>
      selectedParticipants.includes(participant.id) && participant.boatId == null,
  );
  const applicablePaymentLinkKinds = getSharedApplicablePaymentLinkKinds(
    selectedEventParticipants,
  );
  const paymentLinkKindLabels: Record<PaymentLinkKind, string> = {
    deposit: "Deposit",
    balance: "Balance",
    full: "Full amount",
  };
  const selectedPendingParticipants = pendingParticipants.filter((participant) =>
    selectedParticipants.includes(participant.id),
  );
  const selectedBalanceEligibleParticipants = balanceEligibleParticipants.filter(
    (participant) => selectedParticipants.includes(participant.id),
  );
  const takeBalanceDialogParticipants =
    takeBalanceDialogMode === "selected"
      ? selectedBalanceEligibleParticipants
      : takeBalanceDialogMode === "all"
        ? balanceEligibleParticipants
        : [];
  const takeBalanceDialogTotalCents = takeBalanceDialogParticipants.reduce(
    (sum, participant) =>
      sum +
      ((participant.paymentAmountCents ?? 0) -
        (participant.depositAmountCents ?? 0)),
    0,
  );

  const handleBulkApprove = async () => {
    if (selectedPendingParticipants.length === 0 || !id) return;

    const participantIds = selectedPendingParticipants.map(
      (participant) => participant.id,
    );
    setLoadingCheckoutId(id);

    try {
      await bulkApproveMutation.mutateAsync({
        params: {
          path: { id },
        },
        body: {
          participantIds,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${participantIds.length} participant(s) approved`,
      });

      setSelectedParticipants((prev) =>
        prev.filter((id) => !participantIds.includes(id)),
      );
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participants",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleBulkApproveBoats = async () => {
    if (selectedBoats.length === 0 || !id) return;

    setLoadingCheckoutId(id);

    try {
      await bulkApproveBoatsMutation.mutateAsync({
        params: {
          path: { id },
        },
        body: {
          boatIds: selectedBoats,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${selectedBoats.length} boat(s) approved`,
      });

      setSelectedBoats([]);
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve boats",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleApproveAll = async () => {
    if (pendingParticipants.length === 0 || !id) return;

    setLoadingCheckoutId(id);

    try {
      await bulkApproveMutation.mutateAsync({
        params: {
          path: { id },
        },
        body: {
          participantIds: pendingParticipants.map((p) => p.id),
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${pendingParticipants.length} participant(s) approved`,
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participants",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleTakeSelectedBalancesClick = () => {
    if (selectedBalanceEligibleParticipants.length === 0) return;
    setTakeBalanceDialogMode("selected");
  };

  const handleTakeAllBalancesClick = () => {
    if (balanceEligibleParticipants.length === 0) return;
    setTakeBalanceDialogMode("all");
  };

  const handleOpenPaymentLinkDialog = () => {
    if (selectedEventParticipants.length === 0) return;
    const kinds = getSharedApplicablePaymentLinkKinds(selectedEventParticipants);
    if (kinds.length === 0) {
      showToast({
        severity: "warn",
        summary: "No payment due",
        detail:
          "None of the selected participants have an applicable deposit, balance, or full payment to collect.",
      });
      return;
    }
    setPaymentLinkKind(kinds[0]);
    setPaymentLinkUrl(null);
    setPaymentLinkDialogVisible(true);
  };

  const handleGeneratePaymentLink = async () => {
    if (!id || selectedEventParticipants.length === 0) return;

    try {
      const result = await paymentLinkMutation.mutateAsync({
        params: { path: { id } },
        body: {
          paymentKind: paymentLinkKind,
          targetType: "participants",
          targetIds: selectedEventParticipants.map((p) => p.id),
        },
      });
      setPaymentLinkUrl(result.sessionUrl);
      await window.navigator.clipboard.writeText(result.sessionUrl);
      showToast({
        severity: "success",
        summary: "Payment link created",
        detail: "Link copied to clipboard",
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to create payment link",
      });
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!paymentLinkUrl) return;
    await window.navigator.clipboard.writeText(paymentLinkUrl);
    showToast({
      severity: "success",
      summary: "Copied",
      detail: "Payment link copied to clipboard",
    });
  };

  const handleTakeBalanceConfirm = async () => {
    if (!id || takeBalanceDialogParticipants.length === 0) return;

    const participantIds = takeBalanceDialogParticipants.map(
      (participant) => participant.id,
    );
    const mode = takeBalanceDialogMode;
    setTakeBalanceDialogMode(null);
    setLoadingCheckoutId(id);

    try {
      await takeBalanceMutation.mutateAsync({
        params: { path: { id } },
        body: { participantIds },
      });
      showToast({
        severity: "success",
        summary: "Success",
        detail: `${participantIds.length} balance(s) collected`,
      });
      if (mode === "selected") {
        setSelectedParticipants([]);
      }
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to collect balances",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleApproveAllBoats = async () => {
    if (pendingBoats.length === 0 || !id) return;

    setLoadingCheckoutId(id);

    try {
      await bulkApproveBoatsMutation.mutateAsync({
        params: {
          path: { id },
        },
        body: {
          boatIds: pendingBoats.map((b) => b.id),
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: `${pendingBoats.length} boat(s) approved`,
      });
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve boats",
      });
    } finally {
      setLoadingCheckoutId(null);
    }
  };

  const handleApprove = async (participantId: string) => {
    if (approvingParticipantId) {
      return;
    }

    setApprovingParticipantId(participantId);

    try {
      await approveMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: {},
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant approved",
      });
    } catch (error) {
      const { message } = parseApiError(error);
      setApprovingParticipantId(null);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to approve participant",
      });
    } finally {
      setApprovingParticipantId(null);
    }
  };

  const handleReject = (participantId: string) => {
    setParticipantIdToReject(participantId);
    setRejectReason("");
    setRejectDialogVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!participantIdToReject || !rejectReason.trim()) return;

    setRejectDialogVisible(false);
    const participantId = participantIdToReject;
    setParticipantIdToReject(null);

    try {
      await rejectMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: { reason: rejectReason.trim() },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant rejected",
      });

      setRejectReason("");
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to reject participant",
      });
    }
  };

  const handleCancel = (participantId: string) => {
    setParticipantIdToCancel(participantId);
    setCancelReason("");
    setCancelRefund(false);
    setCancelDialogVisible(true);
  };

  const handleCancelConfirm = async () => {
    if (!participantIdToCancel || !cancelReason.trim()) return;

    setCancelDialogVisible(false);
    const participantId = participantIdToCancel;
    const participant = participants.find((p) => p.id === participantId);
    setParticipantIdToCancel(null);

    try {
      const hasPaymentCaptured = participant
        ? hasApprovedPaymentCaptured(participant)
        : false;

      const requestBody: { reason: string; refund?: boolean } = {
        reason: cancelReason.trim(),
      };
      if (hasPaymentCaptured) {
        requestBody.refund = cancelRefund;
      }

      await cancelMutation.mutateAsync({
        params: {
          path: { id: participantId },
        },
        body: requestBody,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Participant cancelled",
      });

      setCancelReason("");
      setCancelRefund(false);
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to cancel participant",
      });
    }
  };

  const handleRefund = () => {
    setRefundReason("");
    setRefundDialogVisible(true);
  };

  const handleOpenCreateUserDialog = () => {
    const first = uniqueParticipantsByName[0];
    setCreateUserPrimaryValue(
      first ? createUserNameKey(first.firstName, first.lastName) : "someoneElse",
    );
    setCreateUserFirstName("");
    setCreateUserLastName("");
    setCreateUserSubUserNames([]);
    setCreateUserDialogVisible(true);
  };

  const handleCreateUserSubmit = async () => {
    if (!id) return;
    const isSomeoneElse = createUserPrimaryType === "someoneElse";
    if (isSomeoneElse && (!createUserFirstName.trim() || !createUserLastName.trim())) {
      showToast({
        severity: "warn",
        summary: "Validation",
        detail: "First name and last name are required for \"Someone else\"",
      });
      return;
    }
    if (!isSomeoneElse && !createUserPrimaryName) {
      showToast({
        severity: "warn",
        summary: "Validation",
        detail: "Please select who the account is for",
      });
      return;
    }

    const primary = isSomeoneElse
      ? { firstName: createUserFirstName.trim(), lastName: createUserLastName.trim() }
      : {
          firstName: createUserPrimaryName!.firstName,
          lastName: createUserPrimaryName!.lastName,
        };

    try {
      await createUserMutation.mutateAsync({
        params: { path: { id } },
        body: {
          primary,
          subUserNames: createUserSubUserNames,
        },
      });
      showToast({
        severity: "success",
        summary: "Success",
        detail: "User account created. A welcome email has been sent to set their password.",
      });
      setCreateUserDialogVisible(false);
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to create user account",
      });
    }
  };

  const createUserSubUserCandidates = (() => {
    if (createUserPrimaryType === "someoneElse") return uniqueParticipantsByName;
    if (!createUserPrimaryName) return [];
    return uniqueParticipantsByName.filter(
      (p: Participant) =>
        createUserNameKey(p.firstName, p.lastName) !== createUserPrimaryValue,
    );
  })();

  const handleRefundConfirm = async () => {
    if (!id) return;

    setRefundDialogVisible(false);
    const checkoutId = id;

    try {
      await refundMutation.mutateAsync({
        params: {
          path: { id: checkoutId },
        },
        body: {
          reason: refundReason.trim() || undefined,
        },
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Booking refunded",
      });

      setRefundReason("");
    } catch (error) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to refund booking",
      });
    }
  };

  const toggleParticipantSelection = (participantId: string) => {
    setSelectedParticipants((prev) => {
      const isSelected = prev.includes(participantId);
      return isSelected
        ? prev.filter((id) => id !== participantId)
        : [...prev, participantId];
    });
  };

  const toggleAllParticipants = () => {
    const allSelectableSelected =
      selectableParticipants.length > 0 &&
      selectableParticipants.every((participant) =>
        selectedParticipants.includes(participant.id),
      );

    setSelectedParticipants(
      allSelectableSelected
        ? []
        : selectableParticipants.map((participant) => participant.id),
    );
  };

  const toggleBoatSelection = (boatId: string) => {
    setSelectedBoats((prev) => {
      const isSelected = prev.includes(boatId);
      return isSelected
        ? prev.filter((id) => id !== boatId)
        : [...prev, boatId];
    });
  };

  const toggleAllBoats = () => {
    const allSelected =
      pendingBoats.length > 0 &&
      pendingBoats.every((b) => selectedBoats.includes(b.id));

    setSelectedBoats(
      allSelected
        ? []
        : pendingBoats.map((b) => b.id),
    );
  };

  const allSelected =
    selectableParticipants.length > 0 &&
    selectableParticipants.every((participant) =>
      selectedParticipants.includes(participant.id),
    );

  const allBoatsSelected =
    pendingBoats.length > 0 &&
    pendingBoats.every((b) => selectedBoats.includes(b.id));

  if (checkoutQuery.isLoading) {
    return (
      <div className="card">
        <p>Loading booking...</p>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className="card">
        <p>Booking not found</p>
        <BackButton className="mt-2" />
      </div>
    );
  }

  const formatPrice = (cents: number | null | undefined) => {
    if (cents == null || cents === 0) return "Free";
    const currency = checkout.currency ?? "gbp";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const boatRowExpansionTemplate = (boat: (typeof boats)[number]) => {
    const crewParticipants = findCrewForBoat(participants, boat.id);
    const showCrewGroupColumn = Boolean(boat.event?.groupsEnabled);
    return (
      <div className="p-3 bg-gray-50 border-round border-1 border-300">
        <h4 className="mt-0 mb-3">Crew Members</h4>
        <DataTable value={crewParticipants} dataKey="id" className="text-sm">
          <Column
            field="name"
            header="Name"
            body={(p: Participant) => `${p.firstName} ${p.lastName}`}
          />
          <Column
            header="Status"
            body={(p: Participant) => <StatusBadge status={p.status ?? undefined} />}
          />
          {showCrewGroupColumn ? (
            <Column
              header="Group"
              body={(p: Participant) => formatParticipantGroup(p)}
            />
          ) : null}
          <Column
            header="Actions"
            body={(p: Participant) => (
              <Button
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void navigate(`/participants/${p.id}`);
                }}
                aria-label="View"
                title={`View ${p.firstName} ${p.lastName}`}
              />
            )}
          />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <div className="flex align-items-center gap-3 mb-4">
        <BackButton />
        <h1 className="text-2xl font-bold m-0">Booking Details</h1>
      </div>

      <Panel header="Booking Information" className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="mb-3">
              <span className="font-semibold">Booking Reference: </span>
              <span>{checkout.bookingReference || "-"}</span>
            </div>
            <div className="mb-3">
              <span className="font-semibold">Status: </span>
              <CheckoutStatusBadge status={checkout.calculatedStatus} />
            </div>
            <div className="mb-3">
              <span className="font-semibold">Total Amount: </span>
              <span>{formatPrice(checkout.totalCents)}</span>
            </div>
            <div className="mb-3">
              <span className="font-semibold">Booking Email: </span>
              <span>{checkout.bookingEmailAddress || "-"}</span>
            </div>
            <div>
              <span className="font-semibold">Marketing Consent: </span>
              {checkout.marketingConsent ? (
                <Tag value="Consented" severity="success" />
              ) : (
                <Tag value="Not Consented" severity="info" />
              )}
            </div>
            {checkout.userId ? (
              <div className="mt-3">
                <span className="font-semibold">User: </span>
                <a
                  href={`/users/${checkout.userId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate(`/users/${checkout.userId}`);
                  }}
                  className="text-primary hover:underline"
                >
                  {user
                    ? `${user.firstName} ${user.lastName}`.trim() || "View user"
                    : userQuery.isLoading
                      ? "Loading…"
                      : "View user"}
                </a>
              </div>
            ) : null}
          </div>
          <div className="col-12 md:col-6">
            <div className="mb-3">
              <span className="font-semibold">Created: </span>
              <span>
                {checkout.createdAt
                  ? formatDateTime(checkout.createdAt)
                  : "-"}
              </span>
            </div>
            <div className="mb-3">
              <span className="font-semibold">Submitted: </span>
              <span>
                {checkout.submittedAt
                  ? formatDateTime(checkout.submittedAt)
                  : "-"}
              </span>
            </div>
            <div>
              <span className="font-semibold">Participants: </span>
              <span>
                {participants.length} total ({pendingParticipants.length} pending,{" "}
                {approvedParticipants.length} approved)
              </span>
            </div>
          </div>
        </div>
        {!checkout.userId && checkout.bookingEmailAddress && (
          <div className="mt-3">
            <Button
              label="Create User Account"
              icon="pi pi-user-plus"
              className="p-button-outlined"
              onClick={handleOpenCreateUserDialog}
            />
          </div>
        )}
      </Panel>

        {eventParticipants.length > 0 && (
          <Panel
            header={
              <div className="flex justify-content-between align-items-center w-full flex-wrap gap-2">
                <span>Participants</span>
                <div className="flex gap-2 ml-3 flex-wrap">
                  {pendingParticipants.length > 0 && (
                    <>
                      <Button
                        label="Approve Selected"
                        icon="pi pi-check"
                        className="p-button-success p-button-sm"
                        onClick={() => void handleBulkApprove()}
                        disabled={
                          selectedPendingParticipants.length === 0 ||
                          loadingCheckoutId === id
                        }
                        loading={loadingCheckoutId === id}
                      />
                      <Button
                        label="Approve All"
                        icon="pi pi-check-circle"
                        className="p-button-success p-button-sm"
                        onClick={() => void handleApproveAll()}
                        disabled={loadingCheckoutId === id}
                        loading={loadingCheckoutId === id}
                      />
                    </>
                  )}
                  {balanceEligibleParticipants.length > 0 && (
                    <>
                      <Button
                        label="Take Selected Balances"
                        icon="pi pi-wallet"
                        className="p-button-info p-button-sm"
                        onClick={handleTakeSelectedBalancesClick}
                        disabled={
                          selectedBalanceEligibleParticipants.length === 0 ||
                          loadingCheckoutId === id
                        }
                        loading={loadingCheckoutId === id}
                      />
                      <Button
                        label="Take All Balances"
                        icon="pi pi-wallet"
                        className="p-button-info p-button-sm"
                        onClick={handleTakeAllBalancesClick}
                        disabled={loadingCheckoutId === id}
                        loading={loadingCheckoutId === id}
                      />
                    </>
                  )}
                  <Button
                    label="Payment Link"
                    icon="pi pi-link"
                    className="p-button-outlined p-button-sm"
                    onClick={handleOpenPaymentLinkDialog}
                    disabled={
                      selectedEventParticipants.length === 0 ||
                      paymentLinkMutation.isPending
                    }
                    loading={paymentLinkMutation.isPending}
                  />
                  {hasApprovedWithPayment && (
                    <Button
                      label="Refund Booking"
                      icon="pi pi-money-bill"
                      className="p-button-warning p-button-sm"
                      onClick={handleRefund}
                    />
                  )}
                </div>
              </div>
            }
            className="mb-4"
          >
              <DataTable
                key={`participants-${selectedParticipants.length}`}
                value={eventParticipants}
                dataKey="id"
                className="text-sm"
                emptyMessage="No participants"
                responsiveLayout="scroll"
                onRowClick={(e) => {
                // Prevent row selection when clicking on action buttons
                if (
                  e.originalEvent &&
                  (e.originalEvent.target as HTMLElement).closest(".p-button")
                ) {
                  e.originalEvent.stopPropagation();
                }
              }}
            >
          <Column
            header={
              selectableParticipants.length > 0 ? (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    onChange={() => {
                      toggleAllParticipants();
                    }}
                  />
                </div>
              ) : null
            }
            body={(participant: Participant) => {
              const isSelectable =
                participant.status === "PENDING" ||
                isEligibleForSharedBalanceCollection(participant);
              if (!isSelectable) return null;
              const isChecked = selectedParticipants.includes(participant.id);
              return (
                <Checkbox
                  inputId={`participant-checkbox-${participant.id}`}
                  checked={isChecked}
                  onChange={(e) => {
                    e.originalEvent?.stopPropagation();
                    toggleParticipantSelection(participant.id);
                  }}
                />
              );
            }}
            style={{ width: "3rem" }}
          />
          <Column
            field="name"
            header="Name"
            body={(participant: Participant) =>
              `${participant.firstName} ${participant.lastName}`
            }
          />
          <Column
            header="Status"
            body={(participant: Participant) => (
              <div className="flex align-items-center gap-2 flex-wrap">
                <StatusBadge status={participant.status ?? undefined} />
                {isBalanceCronExcluded(participant) && (
                  <Tag value="Auto-collect off" severity="warning" />
                )}
              </div>
            )}
          />
          <Column
            header="Event"
            body={(participant: Participant) => {
              const event = participant.event;
              if (!event) return "Unknown";
              const eventTitle = event.title || "Unknown";
              const startDate = event.startsAt
                ? formatDateTime(event.startsAt)
                : null;
              return (
                <div className="flex flex-column gap-1">
                  <span>{eventTitle}</span>
                  {startDate && (
                    <span className="text-sm text-gray-600">{startDate}</span>
                  )}
                </div>
              );
            }}
          />
          {showGroupColumn ? (
            <Column
              header="Group"
              body={(participant: Participant) => formatParticipantGroup(participant)}
            />
          ) : null}
          <Column
            header="Amount"
            body={(participant: Participant) => {
              const amount = participant.paymentAmountCents ?? 0;
              const formatted = (amount / 100).toFixed(2);
              return amount === 0 ? "Free" : `£${formatted}`;
            }}
          />
          <Column
            header="Actions"
            headerStyle={{ textAlign: "center" }}
            bodyStyle={{ textAlign: "center" }}
            body={(participant: Participant) => {
              const menuId = `menu-${participant.id}`;
              
              const menuItems: MenuItem[] = [];
              
              if (!participant.boatId) {
                if (participant.status === "PENDING") {
                  menuItems.push({
                    label: "Approve",
                    icon: "pi pi-check",
                    command: () => {
                      void handleApprove(participant.id);
                    },
                    disabled: approvingParticipantId === participant.id || !!approvingParticipantId,
                  });
                  menuItems.push({
                    label: "Reject",
                    icon: "pi pi-times",
                    command: () => {
                      handleReject(participant.id);
                    },
                  });
                  menuItems.push({
                    label: "Cancel",
                    icon: "pi pi-ban",
                    command: () => {
                      handleCancel(participant.id);
                    },
                  });
                } else if (participant.status === "APPROVED") {
                  menuItems.push({
                    label: "Cancel",
                    icon: "pi pi-ban",
                    command: () => {
                      handleCancel(participant.id);
                    },
                  });
                }
              }

              return (
                <div className="flex align-items-center justify-content-center gap-2">
                  <Button
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      void navigate(`/participants/${participant.id}`);
                    }}
                    aria-label="View"
                  />
                  {menuItems.length > 0 && (
                    <>
                      <Menu
                        ref={(el) => {
                          menuRefs.current[participant.id] = el;
                        }}
                        model={menuItems}
                        popup
                        id={menuId}
                      />
                      <Button
                        icon="pi pi-ellipsis-v"
                        className="p-button-text p-button-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const ref = menuRefs.current[participant.id];
                          if (ref) {
                            ref.toggle(e);
                          }
                        }}
                        aria-label="Actions"
                        aria-controls={menuId}
                        aria-haspopup
                      />
                    </>
                  )}
                </div>
              );
            }}
          />
        </DataTable>
      </Panel>
      )}

      {boats.length > 0 && (
        <Panel
          header={
            <div className="flex justify-content-between align-items-center w-full flex-wrap gap-2">
              <span>Boats</span>
              <div className="flex gap-2 ml-3 flex-wrap">
                {pendingBoats.length > 0 && (
                  <>
                    <Button
                      label="Approve Selected"
                      icon="pi pi-check"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleBulkApproveBoats()}
                      disabled={
                        selectedBoats.length === 0 ||
                        loadingCheckoutId === id
                      }
                      loading={loadingCheckoutId === id}
                    />
                    <Button
                      label="Approve All"
                      icon="pi pi-check-circle"
                      className="p-button-success p-button-sm"
                      onClick={() => void handleApproveAllBoats()}
                      disabled={loadingCheckoutId === id}
                      loading={loadingCheckoutId === id}
                    />
                  </>
                )}
              </div>
            </div>
          }
          className="mb-4"
        >
          <DataTable
            key={`boats-${selectedBoats.length}`}
            value={boats}
            dataKey="id"
            className="text-sm"
            emptyMessage="No boats"
            responsiveLayout="scroll"
            expandedRows={expandedBoatRows || {}}
            onRowToggle={(e) => setExpandedBoatRows(e.data as Record<string, boolean>)}
            rowExpansionTemplate={boatRowExpansionTemplate}
          >
            <Column expander style={{ width: "3rem" }} />
            <Column
              header={
                pendingBoats.length > 0 ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Checkbox
                      checked={allBoatsSelected}
                      onChange={() => {
                        toggleAllBoats();
                      }}
                    />
                  </div>
                ) : null
              }
              body={(boat: (typeof boats)[number]) => {
                const isPending = boatIsPending(boat);
                if (!isPending) return null;
                const isChecked = selectedBoats.includes(boat.id);
                return (
                  <Checkbox
                    inputId={`boat-checkbox-${boat.id}`}
                    checked={isChecked}
                    onChange={(e) => {
                      e.originalEvent?.stopPropagation();
                      toggleBoatSelection(boat.id);
                    }}
                  />
                );
              }}
              style={{ width: "3rem" }}
            />
            <Column
              header="Event"
              body={(boat: (typeof boats)[number]) => {
                const event = boat.event;
                if (!event) return "Unknown";
                const eventTitle = event.title || "Unknown";
                const startDate = event.startsAt
                  ? formatDateTime(event.startsAt)
                  : null;
                return (
                  <div className="flex flex-column gap-1">
                    <span>{eventTitle}</span>
                    {startDate && (
                      <span className="text-xs text-500">{startDate}</span>
                    )}
                  </div>
                );
              }}
            />
            <Column
              header="Boat"
              body={(boat: (typeof boats)[number]) =>
                `${boat.class} - ${boat.sailNumber}`
              }
            />
            <Column
              header="Status"
              body={(boat: (typeof boats)[number]) => (
                <StatusBadge status={boat.status ?? undefined} />
              )}
            />
            <Column
              header="Price point"
              body={(boat: (typeof boats)[number]) => {
                const pricePoint = boat.event?.pricePoints?.find(
                  (pp) => pp.id === boat.eventPricePointId,
                );
                return pricePoint?.label ?? "—";
              }}
            />
            <Column
              header="Amount"
              body={(boat: (typeof boats)[number]) => {
                const amount = boat.paymentAmountCents ?? 0;
                const formatted = (amount / 100).toFixed(2);
                return amount === 0 ? "Free" : `£${formatted}`;
              }}
            />
          </DataTable>
        </Panel>
      )}

      {/* Reject Dialog */}
      <Dialog
        header="Reject Participant"
        visible={rejectDialogVisible}
        style={{ width: "500px" }}
        onHide={() => {
          setRejectDialogVisible(false);
          setRejectReason("");
          setParticipantIdToReject(null);
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setRejectDialogVisible(false);
                setRejectReason("");
                setParticipantIdToReject(null);
              }}
            />
            <Button
              label="Reject"
              icon="pi pi-times"
              className="p-button-danger"
              onClick={() => {
                void handleRejectConfirm();
              }}
              disabled={!rejectReason.trim()}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField id="reject-reason" label="Reason for Rejection" required>
            <InputTextarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for rejecting this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>
        </div>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        header="Cancel Participant"
        visible={cancelDialogVisible}
        style={{ width: "500px" }}
        onHide={() => {
          setCancelDialogVisible(false);
          setCancelReason("");
          setCancelRefund(false);
          setParticipantIdToCancel(null);
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setCancelDialogVisible(false);
                setCancelReason("");
                setCancelRefund(false);
                setParticipantIdToCancel(null);
              }}
              disabled={cancelMutation.isPending}
            />
            <Button
              label="Cancel Participant"
              icon="pi pi-ban"
              className="p-button-warning"
              onClick={() => {
                void handleCancelConfirm();
              }}
              loading={cancelMutation.isPending}
              disabled={!cancelReason.trim() || cancelMutation.isPending}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <FormField
            id="cancel-reason"
            label="Reason for Cancellation"
            required
          >
            <InputTextarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={5}
              placeholder="Please provide a reason for cancelling this participant..."
              className="w-full"
              style={formStyles.input}
              autoFocus
            />
          </FormField>

          {participantIdToCancel && (() => {
            const participant = participants.find(
              (p) => p.id === participantIdToCancel,
            );
            if (!participant || !hasApprovedPaymentCaptured(participant)) {
              return null;
            }

            const hasBalancePayment = hasApprovedBalancePayment(participant);
            const hasDepositPayment = hasApprovedDepositPayment(participant);

            return (
              <div
                className="flex align-items-center gap-2"
                style={{ marginTop: "1rem" }}
              >
                <Checkbox
                  inputId="cancel-refund"
                  checked={cancelRefund}
                  onChange={(e) => setCancelRefund(e.checked ?? false)}
                />
                <label htmlFor="cancel-refund" className="cursor-pointer">
                  Issue refund
                  {hasDepositPayment && hasBalancePayment
                    ? " (deposit and balance)"
                    : hasDepositPayment
                      ? " (deposit)"
                      : " (balance)"}
                </label>
              </div>
            );
          })()}
        </div>
      </Dialog>

      {/* Take Balance Dialog */}
      <Dialog
        header={
          takeBalanceDialogMode === "selected"
            ? "Take Selected Balances"
            : "Take All Balances"
        }
        visible={takeBalanceDialogMode != null}
        style={{ width: "600px" }}
        onHide={() => setTakeBalanceDialogMode(null)}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setTakeBalanceDialogMode(null)}
              disabled={takeBalanceMutation.isPending}
            />
            <Button
              label="Take Balances"
              icon="pi pi-wallet"
              className="p-button-info"
              onClick={() => {
                void handleTakeBalanceConfirm();
              }}
              loading={takeBalanceMutation.isPending}
              disabled={
                takeBalanceMutation.isPending ||
                takeBalanceDialogParticipants.length === 0
              }
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <p className="mb-3">
            Are you sure you want to charge a total of{" "}
            <strong>{formatPrice(takeBalanceDialogTotalCents)}</strong> for{" "}
            {takeBalanceDialogParticipants.length} participant
            {takeBalanceDialogParticipants.length === 1 ? "" : "s"}?
          </p>
          <p className="text-sm text-gray-600 mb-3">
            This will collect the remaining balance after deposit for each
            listed participant using the booking payment method.
          </p>
          <ul className="m-0 pl-3">
            {takeBalanceDialogParticipants.map((participant) => {
              const balanceCents =
                (participant.paymentAmountCents ?? 0) -
                (participant.depositAmountCents ?? 0);
              return (
                <li key={participant.id} className="mb-1">
                  {participant.firstName} {participant.lastName}:{" "}
                  <strong>{formatPrice(balanceCents)}</strong>
                </li>
              );
            })}
          </ul>
        </div>
      </Dialog>

      <Dialog
        header="Generate Payment Link"
        visible={paymentLinkDialogVisible}
        onHide={() => {
          setPaymentLinkDialogVisible(false);
          setPaymentLinkUrl(null);
        }}
        style={{ width: "32rem" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Close"
              className="p-button-text"
              onClick={() => {
                setPaymentLinkDialogVisible(false);
                setPaymentLinkUrl(null);
              }}
              disabled={paymentLinkMutation.isPending}
            />
            {paymentLinkUrl ? (
              <Button
                label="Copy Link"
                icon="pi pi-copy"
                onClick={() => void handleCopyPaymentLink()}
              />
            ) : (
              <Button
                label="Generate"
                icon="pi pi-link"
                onClick={() => void handleGeneratePaymentLink()}
                loading={paymentLinkMutation.isPending}
                disabled={
                  paymentLinkMutation.isPending ||
                  selectedEventParticipants.length === 0 ||
                  applicablePaymentLinkKinds.length === 0 ||
                  !applicablePaymentLinkKinds.includes(paymentLinkKind)
                }
              />
            )}
          </div>
        }
      >
        <p className="m-0 mb-3">
          Create a Stripe payment link for{" "}
          {selectedEventParticipants.length} selected participant
          {selectedEventParticipants.length === 1 ? "" : "s"}.
        </p>
        {applicablePaymentLinkKinds.length === 0 ? (
          <p className="m-0 text-color-secondary">
            No applicable payment types for the current selection.
          </p>
        ) : (
          <div className="flex flex-column gap-2 mb-3">
            {applicablePaymentLinkKinds.map((value) => (
              <div key={value} className="flex align-items-center gap-2">
                <RadioButton
                  inputId={`payment-link-kind-${value}`}
                  name="paymentLinkKind"
                  value={value}
                  checked={paymentLinkKind === value}
                  onChange={() => {
                    setPaymentLinkKind(value);
                    setPaymentLinkUrl(null);
                  }}
                />
                <label htmlFor={`payment-link-kind-${value}`}>
                  {paymentLinkKindLabels[value]}
                </label>
              </div>
            ))}
          </div>
        )}
        {paymentLinkUrl && (
          <InputText value={paymentLinkUrl} readOnly className="w-full" />
        )}
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        header="Refund Booking"
        visible={refundDialogVisible}
        style={{ width: "600px" }}
        onHide={() => {
          setRefundDialogVisible(false);
          setRefundReason("");
        }}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setRefundDialogVisible(false);
                setRefundReason("");
              }}
            />
            <Button
              label="Refund"
              icon="pi pi-money-bill"
              className="p-button-warning"
              onClick={() => {
                void handleRefundConfirm();
              }}
              loading={refundMutation.isPending}
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <div className="mb-3">
            <p className="font-semibold mb-2">
              This will refund all approved participants in this booking.
            </p>
            <p className="text-sm text-gray-600">
              All participants will be cancelled and their payments refunded.
            </p>
          </div>
          <FormField id="refund-reason" label="Reason (optional)">
            <InputTextarea
              id="refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={5}
              placeholder="Optional reason for the refund..."
              className="w-full"
              style={formStyles.input}
            />
          </FormField>
        </div>
      </Dialog>

      {/* Create User Account Dialog */}
      <Dialog
        header="Create User Account"
        visible={createUserDialogVisible}
        style={{ width: "500px" }}
        onHide={() => setCreateUserDialogVisible(false)}
        contentStyle={{ padding: "0" }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setCreateUserDialogVisible(false)}
              disabled={createUserMutation.isPending}
            />
            <Button
              label="Create Account"
              icon="pi pi-user-plus"
              onClick={() => void handleCreateUserSubmit()}
              loading={createUserMutation.isPending}
              disabled={
                createUserMutation.isPending ||
                (createUserPrimaryType === "someoneElse" &&
                  (!createUserFirstName.trim() || !createUserLastName.trim())) ||
                (createUserPrimaryType === "participant" && !createUserPrimaryName)
              }
            />
          </div>
        }
      >
        <div style={{ padding: "0 1rem 1rem 1rem" }}>
          <p className="mb-3 text-sm text-color-secondary">
            An account will be created for the booking email and a welcome email
            sent so they can set their password.
          </p>
          <FormField id="create-user-who" label="Who is this account for?" required>
            <div className="flex flex-column gap-2 mt-1">
              {uniqueParticipantsByName.map((p: Participant) => {
                const nameKey = createUserNameKey(p.firstName, p.lastName);
                return (
                  <div key={nameKey} className="flex align-items-center gap-2">
                    <RadioButton
                      inputId={`create-user-participant-${nameKey}`}
                      name="createUserPrimary"
                      value={nameKey}
                      checked={createUserPrimaryValue === nameKey}
                      onChange={() => setCreateUserPrimaryValue(nameKey)}
                    />
                    <label
                      htmlFor={`create-user-participant-${nameKey}`}
                      className="cursor-pointer"
                    >
                      {p.firstName} {p.lastName}
                    </label>
                  </div>
                );
              })}
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="create-user-someone-else"
                  name="createUserPrimary"
                  value="someoneElse"
                  checked={createUserPrimaryValue === "someoneElse"}
                  onChange={() => setCreateUserPrimaryValue("someoneElse")}
                />
                <label
                  htmlFor="create-user-someone-else"
                  className="cursor-pointer"
                >
                  Someone else (e.g. booker)
                </label>
              </div>
              {createUserPrimaryType === "someoneElse" && (
                <div className="grid mt-2 ml-4">
                  <div className="col-6">
                    <FormField id="create-user-first" label="First name" required>
                      <InputText
                        id="create-user-first"
                        value={createUserFirstName}
                        onChange={(e) => setCreateUserFirstName(e.target.value)}
                        className="w-full"
                        style={formStyles.input}
                      />
                    </FormField>
                  </div>
                  <div className="col-6">
                    <FormField id="create-user-last" label="Last name" required>
                      <InputText
                        id="create-user-last"
                        value={createUserLastName}
                        onChange={(e) => setCreateUserLastName(e.target.value)}
                        className="w-full"
                        style={formStyles.input}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </div>
          </FormField>

          {createUserSubUserCandidates.length > 0 && (
            <FormField
              id="create-user-subusers"
              label="Save other participants as sub-users"
              className="mt-3"
            >
              <div className="flex flex-column gap-2 mt-1">
                {createUserSubUserCandidates.map((p: Participant) => {
                  const nameKey = createUserNameKey(p.firstName, p.lastName);
                  const isChecked = createUserSubUserNames.some(
                    (n) =>
                      n.firstName === p.firstName && n.lastName === p.lastName,
                  );
                  return (
                    <div key={nameKey} className="flex align-items-center gap-2">
                      <Checkbox
                        inputId={`create-user-sub-${nameKey}`}
                        checked={isChecked}
                        onChange={(e) => {
                          const checked = e.checked ?? false;
                          setCreateUserSubUserNames((prev) =>
                            checked
                              ? [...prev, { firstName: p.firstName, lastName: p.lastName }]
                              : prev.filter(
                                  (n) =>
                                    n.firstName !== p.firstName ||
                                    n.lastName !== p.lastName,
                                ),
                          );
                        }}
                      />
                      <label
                        htmlFor={`create-user-sub-${nameKey}`}
                        className="cursor-pointer"
                      >
                        {p.firstName} {p.lastName}
                      </label>
                    </div>
                  );
                })}
              </div>
            </FormField>
          )}
        </div>
      </Dialog>
    </div>
  );
}

